import { FC, useState } from "react";
import { Alert, Button, Table } from "react-bootstrap";
import Loader from "src/components/common/loader/loader";
import ModalDelete from "src/components/common/ModalDelete";
import { PopulationSegment } from "@models/populationSegment.model";
import useOrganizationSegments from "../hooks/useOrganizationSegments";
import SegmentFormModal, { SegmentFormValues } from "./SegmentFormModal";

interface Props {
  organizationId: number;
}

const OrganizationSegmentsTab: FC<Props> = ({ organizationId }) => {
  const { segments, isLoading, isSaving, error, createSegment, updateSegment, deleteSegment } =
    useOrganizationSegments(organizationId);

  const [formModal, setFormModal] = useState<{ segment: PopulationSegment | null } | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<PopulationSegment | null>(null);

  // Names used for instant duplicate feedback in the form — exclude the row being edited.
  const otherNames = (editing: PopulationSegment | null) =>
    segments
      .filter((s) => s.id !== editing?.id)
      .map((s) => s.name.trim().toLowerCase());

  const handleSave = async (values: SegmentFormValues) => {
    if (formModal?.segment) {
      await updateSegment(formModal.segment.id, values);
    } else {
      await createSegment(values);
    }
    setFormModal(null); // reached only on success; on error the modal stays open
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteSegment(deleteTarget.id);
    } finally {
      setDeleteTarget(null);
    }
  };

  if (isLoading && segments.length === 0) {
    return <Loader />;
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h5 className="mb-1">Population Segments</h5>
          <p className="text-muted small mb-0">
            Define the named populations this organization can target in launches.
          </p>
        </div>
        <Button variant="primary" size="sm" onClick={() => setFormModal({ segment: null })} disabled={isSaving}>
          <i className="bi bi-plus-lg me-1" />
          Add segment
        </Button>
      </div>

      {error && <Alert variant="danger" className="mb-3">{error}</Alert>}

      {segments.length === 0 ? (
        <div className="text-muted text-center py-5">
          No segments yet. Click <span className="fw-semibold">Add segment</span> to define one.
        </div>
      ) : (
        <Table hover responsive className="align-middle">
          <thead>
            <tr>
              <th>Name</th>
              <th className="text-end">Actions</th>
            </tr>
          </thead>
          <tbody>
            {segments.map((seg) => (
              <tr key={seg.id}>
                <td className="fw-semibold">{seg.name}</td>
                <td className="text-end">
                  <Button
                    variant="link"
                    size="sm"
                    className="text-decoration-none"
                    onClick={() => setFormModal({ segment: seg })}
                    disabled={isSaving}
                  >
                    <i className="bi bi-pencil me-1" />
                    Edit
                  </Button>
                  <Button
                    variant="link"
                    size="sm"
                    className="text-danger text-decoration-none"
                    onClick={() => setDeleteTarget(seg)}
                    disabled={isSaving}
                  >
                    <i className="bi bi-trash me-1" />
                    Delete
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      {formModal && (
        <SegmentFormModal
          show
          segment={formModal.segment}
          existingNames={otherNames(formModal.segment)}
          isSaving={isSaving}
          onClose={() => setFormModal(null)}
          onSave={handleSave}
        />
      )}

      <ModalDelete
        title="Segment"
        objectLabel={deleteTarget?.name ?? ""}
        deleteFn={handleDelete}
        visible={deleteTarget !== null}
        toggleVisibility={(visible: boolean) => {
          if (!visible) setDeleteTarget(null);
        }}
        isLoading={isSaving}
      />
    </div>
  );
};

export default OrganizationSegmentsTab;
